<div class="container-fluid">
  <hc-page-heading variant="subpage" [backLabel]="'COMMON.BACK' | translate" (back)="goBack()"
    [title]="'ONBOARDING_REQUESTS.REQUEST_DETAILS' | translate"
    [description]="request ? (('ONBOARDING_REQUESTS.SUBMITTED_ON' | translate) + ' ' + (request.created_at | date:'dd.MM.yyyy HH:mm')) : ''">
    <div pageActions class="d-flex align-items-center gap-2">
      @if (request) {
        <hc-badge [color]="getStatusVariant(request.status)" variant="light" size="small">
          {{ 'ONBOARDING_REQUESTS.STATUS.' + request.status.toUpperCase() | translate }}
        </hc-badge>
      }
    </div>
  </hc-page-heading>

  <!-- Loading State -->
  @if (isLoading) {
    <div class="text-center py-5 mt-3">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'COMMON.LOADING' | translate }}</span>
      </div>
    </div>
  }

  <!-- Content -->
  @if (!isLoading && request) {
    <div class="row mt-3">
      <!-- Main Content -->
      <div class="col-lg-8">
        <!-- Hotel Information Card -->
        <div class="card card-v2 mb-4">
          <div class="card-header">
            <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUEST.HOTEL_INFO' | translate }}</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.HOTEL_NAME' | translate }}</label>
                <p class="mb-0 fw-medium">{{ request.hotel_name }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.CITY' | translate }}</label>
                <p class="mb-0 fw-medium">
                  {{ request.hotel_city }}
                  @if (request.hotel_country) {
                    <span>, {{ request.hotel_country }}</span>
                  }
                </p>
              </div>
              @if (request.hotel_website) {
                <div class="col-md-6">
                  <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.WEBSITE' | translate }}</label>
                  <p class="mb-0">
                    <a [href]="request.hotel_website" target="_blank" class="text-primary">
                      {{ request.hotel_website }}
                      <lucide-icon name="external-link" class="ms-1"></lucide-icon>
                    </a>
                  </p>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Contact Information Card -->
        <div class="card card-v2 mb-4">
          <div class="card-header">
            <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUEST.CONTACT_INFO' | translate }}</h5>
          </div>
          <div class="card-body">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.CONTACT_NAME' | translate }}</label>
                <p class="mb-0 fw-medium">{{ request.contact_name }}</p>
              </div>
              <div class="col-md-6">
                <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.CONTACT_EMAIL' | translate }}</label>
                <p class="mb-0">
                  <a [href]="'mailto:' + request.contact_email" class="text-primary">
                    {{ request.contact_email }}
                  </a>
                </p>
              </div>
              @if (request.contact_phone) {
                <div class="col-md-6">
                  <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.CONTACT_PHONE' | translate }}</label>
                  <p class="mb-0">
                    <a [href]="'tel:' + request.contact_phone" class="text-primary">
                      {{ request.contact_phone }}
                    </a>
                  </p>
                </div>
              }
              @if (request.contact_position) {
                <div class="col-md-6">
                  <label class="form-label text-muted">{{ 'ONBOARDING_REQUEST.POSITION' | translate }}</label>
                  <p class="mb-0 fw-medium">{{ formatContactPosition(request.contact_position) }}</p>
                </div>
              }
            </div>
          </div>
        </div>

        <!-- Message Card -->
        @if (request.message) {
          <div class="card card-v2 mb-4">
            <div class="card-header">
              <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUEST.MESSAGE' | translate }}</h5>
            </div>
            <div class="card-body">
              <p class="mb-0 preserve-whitespace">{{ request.message }}</p>
            </div>
          </div>
        }

        <!-- Referral Source Card -->
        @if (request.how_did_you_hear) {
          <div class="card card-v2 mb-4">
            <div class="card-header">
              <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUEST.HOW_DID_YOU_HEAR' | translate }}</h5>
            </div>
            <div class="card-body">
              <p class="mb-0">{{ formatReferralSource(request.how_did_you_hear) }}</p>
            </div>
          </div>
        }

        <!-- Rejection Reason Card -->
        @if (request.status === 'rejected' && request.rejection_reason) {
          <div class="card card-v2 mb-4 border-danger">
            <div class="card-header bg-danger-subtle">
              <h5 class="card-title mb-0 text-danger">{{ 'ONBOARDING_REQUESTS.REJECTION_REASON' | translate }}</h5>
            </div>
            <div class="card-body">
              <p class="mb-0 text-danger">{{ request.rejection_reason }}</p>
            </div>
          </div>
        }
      </div>

      <!-- Sidebar -->
      <div class="col-lg-4">
        <!-- Internal Notes Card -->
        <div class="card card-v2 mb-4">
          <div class="card-header">
            <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUESTS.INTERNAL_NOTES' | translate }}</h5>
          </div>
          <div class="card-body">
            @if (canEdit && canProcess()) {
              <hc-textarea
                [(ngModel)]="internalNotes"
                [placeholder]="'ONBOARDING_REQUESTS.NOTES_PLACEHOLDER' | translate"
                [rows]="5">
              </hc-textarea>
              <div class="mt-3">
                <hc-button
                  variant="primary"
                  (clicked)="saveNotes()"
                  [disabled]="isSavingNotes"
                  [loading]="isSavingNotes">
                  {{ isSavingNotes ? ('COMMON.SAVING' | translate) : ('COMMON.SAVE_NOTES' | translate) }}
                </hc-button>
              </div>
            } @else {
              @if (!request.internal_notes) {
                <p class="mb-0 text-muted">{{ 'ONBOARDING_REQUESTS.NO_NOTES' | translate }}</p>
              }
              @if (request.internal_notes) {
                <p class="mb-0 preserve-whitespace">{{ request.internal_notes }}</p>
              }
            }
          </div>
        </div>

        <!-- Linked Hotel Card (for completed requests) -->
        @if (request.status === 'completed' && request.created_hotel_id) {
          <div class="card card-v2 mb-4">
            <div class="card-header">
              <h5 class="card-title mb-0">{{ 'ONBOARDING_REQUESTS.LINKED_HOTEL' | translate }}</h5>
            </div>
            <div class="card-body">
              <hc-button variant="secondary" leftIcon="building-2" (clicked)="viewHotel()">
                {{ 'ONBOARDING_REQUESTS.VIEW_HOTEL' | translate }}
              </hc-button>
            </div>
          </div>
        }

        <!-- Actions Card -->
        @if (canEdit && canProcess()) {
          <div class="card card-v2">
            <div class="card-header">
              <h5 class="card-title mb-0">{{ 'COMMON.ACTIONS' | translate }}</h5>
            </div>
            <div class="card-body">
              <div class="d-grid gap-2">
                @if (request.status === 'in_progress') {
                  <hc-button
                    variant="primary"
                    size="md"
                    leftIcon="building-2"
                    (clicked)="createHotelFromRequest()">
                    {{ 'ONBOARDING_REQUESTS.CREATE_HOTEL' | translate }}
                  </hc-button>
                }
                @if (request.status === 'pending') {
                  <hc-button
                    variant="primary"
                    size="md"
                    leftIcon="phone"
                    (clicked)="markAsContacted()">
                    {{ 'ONBOARDING_REQUESTS.MARK_CONTACTED' | translate }}
                  </hc-button>
                }
                @if (request.status === 'contacted') {
                  <hc-button
                    variant="primary"
                    size="md"
                    leftIcon="clock"
                    (clicked)="markAsInProgress()">
                    {{ 'ONBOARDING_REQUESTS.MARK_IN_PROGRESS' | translate }}
                  </hc-button>
                }
                <hc-button
                  variant="destructive-secondary"
                  leftIcon="circle-x"
                  (clicked)="openRejectModal()">
                  {{ 'ONBOARDING_REQUESTS.REJECT' | translate }}
                </hc-button>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }
</div>

<!-- Reject Modal -->
@if (isRejectModalOpen) {
  <!-- eslint-disable-next-line @angular-eslint/template/interactive-supports-focus -->
  <div class="modal-backdrop fade show" (click)="closeRejectModal()"></div>
  <div class="modal fade show d-block" tabindex="-1">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ 'ONBOARDING_REQUESTS.REJECT_REQUEST' | translate }}</h5>
          <button type="button" class="btn-close" (click)="closeRejectModal()" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <div class="mb-3">
            <label class="form-label">{{ 'ONBOARDING_REQUESTS.REJECTION_REASON' | translate }} *</label>
            <hc-textarea
              [(ngModel)]="rejectionReason"
              [placeholder]="'ONBOARDING_REQUESTS.REJECTION_REASON_PLACEHOLDER' | translate"
              [rows]="4">
            </hc-textarea>
          </div>
        </div>
        <div class="modal-footer">
          <hc-button variant="secondary" size="md" (clicked)="closeRejectModal()">
            {{ 'COMMON.CANCEL' | translate }}
          </hc-button>
          <hc-button
            variant="danger"
            (clicked)="confirmReject()"
            [disabled]="!rejectionReason.trim() || isRejecting"
            [loading]="isRejecting">
            {{ isRejecting ? ('COMMON.PROCESSING' | translate) : ('ONBOARDING_REQUESTS.CONFIRM_REJECT' | translate) }}
          </hc-button>
        </div>
      </div>
    </div>
  </div>
}
